/**
 * The "Why not?" follow-up pad for opening-bid modules (feature 023, FR-017d).
 *
 * When the learner answers "No" to "Should you open 1♣?", they are asked WHY — insufficient
 * points (should pass) or right strength but wrong bid (should open something else). Only after
 * that click is the answer graded: knowing WHY you do not open is the lesson the client wants,
 * so a bare "No" is not enough on its own.
 *
 * Both options are reasons, not verdict polarity, so they share the neutral card styling rather
 * than the Yes/No green — mirroring {@link YesNoPad} otherwise (button recipe, aria-pressed,
 * brass ring on the chosen option).
 */

import { StepBackButton } from './StepBackButton';

export interface WhyNotPadProps {
  onReason: (reason: 'pass' | 'other') => void;
  disabled?: boolean;
  /** Once picked, the chosen reason is marked so the feedback has context. */
  chosen?: 'pass' | 'other' | null;
  /**
   * Un-answers the "No" this pad is following up, returning the flow to Yes/No (client
   * request, 2026-07-28). On phones the Yes/No pad is swapped out while this one shows, so
   * without it a mis-pressed No would be a dead end there.
   */
  onBack?: () => void;
}

// padswap: slimmer buttons on short single-column viewports, matching YesNoPad, so the
// swapped-in step never stands taller than the pad it replaced.
const BTN =
  'w-full rounded-lg py-3 px-3 text-center text-[14px] font-bold tracking-wide transition-colors ' +
  'border border-line-strong bg-card text-ink shadow-[0_2px_0_#d8ceae] hover:bg-cream ' +
  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary-600 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 padswap:py-2';

export function WhyNotPad({ onReason, disabled = false, chosen = null, onBack }: WhyNotPadProps) {
  return (
    <div className="space-y-3 padswap:space-y-2" role="group" aria-label="Why not?" data-testid="why-not-pad">
      {/* The back control leads the prompt row: first in reading and tab order, because it
          undoes the step the row is asking about. `disabled` freezes it with the reason
          buttons — once "Insufficient points" is in flight the No can no longer be taken back. */}
      <div className="flex items-center gap-2">
        {onBack && (
          <StepBackButton
            onClick={onBack}
            disabled={disabled}
            label="Back to Yes or No"
            testId="why-not-back"
          />
        )}
        <p className="text-[15px] font-semibold text-ink" data-testid="why-not-prompt">
          Why not?
        </p>
      </div>

      <button
        type="button"
        disabled={disabled}
        aria-pressed={chosen === 'pass'}
        onClick={() => onReason('pass')}
        data-testid="why-insufficient-points"
        className={`${BTN} ${chosen === 'pass' ? 'ring-2 ring-brass' : ''}`}
      >
        Insufficient points — should pass
      </button>

      <button
        type="button"
        disabled={disabled}
        aria-pressed={chosen === 'other'}
        onClick={() => onReason('other')}
        data-testid="why-open-other"
        className={`${BTN} ${chosen === 'other' ? 'ring-2 ring-brass' : ''}`}
      >
        Should open something else
      </button>
    </div>
  );
}
